(() => {
  if (window.PawSwingV2) {
    window.PawSwingV2.init(document);
    return;
  }
  const instances = new Map();
  const iconMarkup = '<svg viewBox="0 0 20 20" fill="none"><path d="M3.125 10H16.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path><path class="vertical" d="M10 3.125V16.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path></svg>';

  // Adapted from the original theme AccordionDetails; scoped to article content.
  class ArticleAccordion {
    constructor(details, group, signal) {
      this.details = details;
      this.group = group;
      this.content = details.querySelector('.qa-answer');
      this.targetOpen = details.open;
      this.pending = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      details.classList.add('qa-enhanced');
      this.content.style.height = details.open ? 'auto' : '0px';
      details.querySelector('summary').addEventListener('click', event => {
        event.preventDefault();
        this.setOpen(!this.targetOpen);
      }, { signal });
      this.motion.addEventListener('change', () => {
        if (this.pending && this.motion.matches) this.finish();
      }, { signal });
      this.observer = new ResizeObserver(() => {
        if (this.pending && this.targetOpen) this.setOpen(true);
      });
      this.observer.observe(details.querySelector('.qa-answer-body'));
    }
    setOpen(open) {
      if (open) this.group.forEach(item => {
        if (item !== this && item.targetOpen) item.setOpen(false);
      });
      this.cancel();
      this.targetOpen = open;
      const startHeight = this.content.getBoundingClientRect().height;
      this.content.style.height = `${startHeight}px`;
      if (open) this.details.open = true;
      this.pending = true;
      const endHeight = open ? this.content.scrollHeight : 0;
      if (this.motion.matches || Math.abs(startHeight - endHeight) < 1) {
        this.finish();
        return;
      }
      void this.content.offsetHeight;
      this.onEnd = event => {
        if (event.target === this.content && event.propertyName === 'height') this.finish();
      };
      this.content.addEventListener('transitionend', this.onEnd);
      this.frame = requestAnimationFrame(() => {
        this.content.style.height = `${endHeight}px`;
        this.timeout = setTimeout(() => this.finish(), 600);
      });
    }
    cancel() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.timeout);
      if (this.onEnd) this.content.removeEventListener('transitionend', this.onEnd);
    }
    finish() {
      this.cancel();
      this.pending = false;
      this.details.open = this.targetOpen;
      this.content.style.height = this.targetOpen ? 'auto' : '0px';
    }
    destroy() {
      this.finish();
      this.observer.disconnect();
      this.details.classList.remove('qa-enhanced');
      this.content.style.removeProperty('height');
    }
  }

  function prepareQuestion(details) {
    const summary = details.querySelector(':scope > summary');
    const answer = details.querySelector(':scope > .qa-answer') || summary?.nextElementSibling;
    if (!summary || !answer) return false;
    answer.classList.add('qa-answer');
    if (!answer.querySelector(':scope > .qa-answer-body')) {
      const body = document.createElement('div');
      body.className = 'qa-answer-body';
      body.append(...answer.childNodes);
      answer.append(body);
    }
    if (!summary.querySelector('.qa-question')) {
      const question = document.createElement('span');
      question.className = 'qa-question';
      question.append(...summary.childNodes);
      summary.append(question);
    }
    if (!summary.querySelector('.qa-number')) {
      const number = document.createElement('span');
      number.className = 'qa-number';
      number.setAttribute('aria-hidden', 'true');
      summary.prepend(number);
    }
    if (!summary.querySelector('.qa-toggle')) {
      const icon = document.createElement('span');
      icon.className = 'qa-toggle';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = iconMarkup;
      summary.append(icon);
    }
    return true;
  }

  function qaMarker(node) {
    if (node.nodeType !== Node.ELEMENT_NODE || !node.matches('p,div,h1,h2,h3,h4,h5,h6')) return null;
    if (node.querySelector('p,div,h1,h2,h3,h4,h5,h6,table,ul,ol,details')) return null;
    const match = node.textContent.match(/^[\s\u00a0]*([QA])\s*[:\uFF1A]\s*/i);
    return match ? { kind:match[1].toUpperCase(), length:match[0].length } : null;
  }

  function removeQAPrefix(node, length) {
    const clone = node.cloneNode(true);
    const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
    let text;
    while (length > 0 && (text = walker.nextNode())) {
      const removed = Math.min(length, text.data.length);
      text.data = text.data.slice(removed);
      length -= removed;
    }
    return clone;
  }

  function faqUnits(node) {
    if (node.nodeType !== Node.ELEMENT_NODE || !node.matches('p') || !node.querySelector('br')) return [node.cloneNode(true)];
    const parts = [];
    const range = document.createRange();
    range.setStart(node, 0);
    for (const br of node.querySelectorAll('br')) {
      range.setEndBefore(br);
      const part = node.cloneNode(false);
      part.removeAttribute('id');
      part.append(range.cloneContents());
      parts.push(part);
      range.setStartAfter(br);
    }
    range.setEnd(node, node.childNodes.length);
    const last = node.cloneNode(false);
    last.removeAttribute('id');
    last.append(range.cloneContents());
    parts.push(last);
    return parts.slice(1).some(qaMarker) ? parts : [node.cloneNode(true)];
  }

  function hasAnswerContent(node) {
    return !!node.textContent.trim() || (node.nodeType === Node.ELEMENT_NODE &&
      (node.matches('img,video,audio,iframe,table') || !!node.querySelector('img,video,audio,iframe,table')));
  }

  // Validate a whole FAQ section before replacing anything; malformed content stays readable.
  function convertTextFAQs(content) {
    for (const heading of content.querySelectorAll('h2,h3,h4,h5,h6,p')) {
      if (!content.contains(heading) || heading.closest('.article-qa,table,pre,blockquote,li,details')) continue;
      const label = heading.textContent.trim().replace(/\s+/g, ' ');
      if (!/^(?:FAQs?|Frequently asked questions)$/i.test(label)) continue;
      const level = /^H[2-6]$/.test(heading.tagName) ? Number(heading.tagName[1]) : 2;
      const sources = [];
      for (let node = heading.nextSibling; node; node = node.nextSibling) {
        if (node.nodeType === Node.ELEMENT_NODE &&
          ((/^H[1-6]$/.test(node.tagName) && Number(node.tagName[1]) <= level && !qaMarker(node)) ||
           node.matches('.article-qa'))) break;
        sources.push(node);
      }
      const pairs = [];
      const intro = [];
      let current = null;
      let valid = true;
      for (const node of sources.flatMap(faqUnits)) {
        const marker = qaMarker(node);
        if (marker?.kind === 'Q') {
          if (current && !current.hasAnswer) { valid = false; break; }
          current = { question:removeQAPrefix(node, marker.length), answer:[], hasAnswer:false };
          pairs.push(current);
        } else if (marker?.kind === 'A') {
          if (!current || current.hasAnswer) { valid = false; break; }
          current.hasAnswer = true;
          const answer = removeQAPrefix(node, marker.length);
          if (hasAnswerContent(answer)) current.answer.push(answer);
        } else if (!current) {
          intro.push(node);
        } else if (current.hasAnswer) {
          current.answer.push(node);
        } else if (hasAnswerContent(node)) {
          valid = false;
          break;
        }
      }
      if (!valid || !pairs.length || pairs.some(pair => !pair.question.textContent.trim() ||
        !pair.hasAnswer || !pair.answer.some(hasAnswerContent))) continue;

      const section = document.createElement('section');
      section.className = 'article-qa';
      section.dataset.pjAutoQa = '';
      const list = document.createElement('div');
      list.className = 'qa-list';
      pairs.forEach((pair, index) => {
        const details = document.createElement('details');
        details.open = index === 0;
        const summary = document.createElement('summary');
        summary.append(...pair.question.childNodes);
        const answer = document.createElement('div');
        answer.className = 'qa-answer';
        answer.append(...pair.answer);
        details.append(summary, answer);
        list.append(details);
      });
      heading.before(section);
      if (heading.matches('p')) {
        const title = document.createElement('h2');
        if (heading.id) title.id = heading.id;
        title.append(...heading.childNodes);
        section.append(title);
        heading.remove();
      } else {
        section.append(heading);
      }
      section.append(...intro, list);
      sources.forEach(node => node.remove());
    }
  }

  function mount(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const accordions = [];
    root.querySelectorAll('.pj-content').forEach(convertTextFAQs);
    root.querySelectorAll('.pj-content table').forEach(table => {
      table.removeAttribute('width');
      table.style.removeProperty('min-width');
      if (table.parentElement.classList.contains('table-scroll')) return;
      const region = document.createElement('div');
      region.className = 'table-scroll';
      region.setAttribute('role', 'region');
      region.setAttribute('aria-label', table.caption?.textContent.trim() || 'Article table');
      region.tabIndex = 0;
      table.before(region);
      region.append(table);
    });
    root.querySelectorAll('.article-qa .qa-list').forEach(list => {
      const group = [];
      [...list.querySelectorAll(':scope > details')].filter(prepareQuestion).forEach((details, index) => {
        details.open = index === 0;
        const accordion = new ArticleAccordion(details, group, abort.signal);
        group.push(accordion);
        accordions.push(accordion);
      });
    });
    const progress = root.querySelector('.pj-progress');
    const updateProgress = () => {
      if (!progress) return;
      const bounds = root.getBoundingClientRect();
      const scrollable = Math.max(1, bounds.height - innerHeight);
      progress.style.width = `${Math.max(0, Math.min(100, -bounds.top / scrollable * 100))}%`;
    };
    window.addEventListener('scroll', updateProgress, { passive: true, signal: abort.signal });
    window.addEventListener('resize', updateProgress, { signal: abort.signal });
    updateProgress();
    instances.set(root, () => {
      abort.abort();
      accordions.forEach(accordion => accordion.destroy());
      instances.delete(root);
    });
  }

  function init(scope) {
    if (scope.matches?.('[data-pawswing-v2]')) mount(scope);
    scope.querySelectorAll('[data-pawswing-v2]').forEach(mount);
  }
  window.PawSwingV2 = { init };
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((destroy, root) => {
      if (event.target.contains(root)) destroy();
    });
  });
})();
